import { z } from "zod";

import {
  ExtractAtomicOperationFromDefinition,
  createAtomicOperationDefinition,
} from "../../atomic-operations/index.js";

import { SemanticCap } from "../../semanticCaps.js";
import {
  zAppSessionCellId,
  zExploreCellId,
  zExploreSpecSessionStateId,
} from "../../zod/ZodIdTypeBrands.js";
import { CREATE_APP_SESSION_CELL } from "../appSessionCellOperations.js";

import { EXPLORE_SPEC_SESSION_STATE_FIELDS } from "./commonExploreSpecSessionStateTypes.js";

const CREATE_EXPLORE_SPEC_SESSION_STATE_TYPE =
  "CREATE_EXPLORE_SPEC_SESSION_STATE" as const;

export const CreateExploreSpecSessionStatePayload = z.object({
  exploreSpecSessionStateId: zExploreSpecSessionStateId,
  exploreCellId: zExploreCellId,
  appSessionCellId: zAppSessionCellId,
  shouldRunCell: z.boolean().optional(),
  options: z.object(EXPLORE_SPEC_SESSION_STATE_FIELDS).partial(),
});
export type CreateExploreSpecSessionStatePayload = z.infer<
  typeof CreateExploreSpecSessionStatePayload
>;

export const CREATE_EXPLORE_SPEC_SESSION_STATE =
  createAtomicOperationDefinition({
    type: CREATE_EXPLORE_SPEC_SESSION_STATE_TYPE,
    payloadSchema: CreateExploreSpecSessionStatePayload,
    readAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCap",
          cap: SemanticCap.VIEW_APP_SESSION,
        },
        {
          kind: "hasSomeOfSemanticCapsOnIdArg",
          someOfCaps: [
            SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
            SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
          ],
          idArg: "exploreCellId",
          idType: "ExploreCell",
        },
      ],
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCap",
          cap: SemanticCap.EDIT_APP_SESSION,
        },
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_APP_SESSION,
          idArg: "appSessionCellId",
          idType: "AppSessionCell",
        },
        {
          kind: "hasSomeOfSemanticCapsOnIdArg",
          someOfCaps: [
            SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
            SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
          ],
          idArg: "exploreCellId",
          idType: "ExploreCell",
        },
        {
          kind: "idArgDoesNotExist",
          idArg: "exploreSpecSessionStateId",
          idType: "ExploreSpecSessionState",
        },
      ],
    },
    logSafe: [
      "exploreSpecSessionStateId",
      "exploreCellId",
      "appSessionCellId",
      "shouldRunCell",
    ],
    alwaysSkipUndoBuffer: true,
    conflictId: (op) =>
      `${CREATE_EXPLORE_SPEC_SESSION_STATE_TYPE}-${op.payload.exploreSpecSessionStateId}`,
    creationId: (op) =>
      `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
    create: (args: CreateExploreSpecSessionStatePayload) => ({
      type: CREATE_EXPLORE_SPEC_SESSION_STATE_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type CREATE_EXPLORE_SPEC_SESSION_STATE =
  ExtractAtomicOperationFromDefinition<
    typeof CREATE_EXPLORE_SPEC_SESSION_STATE
  >;
